import { FunctionComponent, useState } from "react";

import { faRotateLeft } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { useSnackBar } from "@/utils/useSnackbar";

import { useAddMissingPipedriveDefaultFieldMappingsMutation } from "@/pages/Private/redux/pipedrive/pipedrive.api";

import { PipedriveEntityType } from "../schema/pipedrive";

/** The tabs that have defaults to add; a lead's and a deal's catalogs are empty (PREC-4081). */
export const PIPEDRIVE_TABS_WITH_DEFAULTS: readonly PipedriveEntityType[] = [
	PipedriveEntityType.PERSON,
	PipedriveEntityType.ORGANIZATION,
];

interface PipedriveAddDefaultsButtonProps {
	/** The tab's entity — only its defaults are added. */
	entityType: PipedriveEntityType;
	ts: (key: string, options?: Record<string, unknown>) => string;
}

/**
 * "Add missing defaults" (PREC-4081): re-adds the default mappings of one tab — the set a fresh
 * connection is seeded with, including the tenant's own custom fields whose label names a
 * Signalist source. The endpoint only fills gaps, so it never overwrites or duplicates a mapping;
 * when nothing was missing that is said, rather than left as a silent no-op.
 */
export const PipedriveAddDefaultsButton: FunctionComponent<PipedriveAddDefaultsButtonProps> = ({
	entityType,
	ts,
}) => {
	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();
	const [addMissingDefaults] = useAddMissingPipedriveDefaultFieldMappingsMutation();
	const [isAdding, setIsAdding] = useState(false);

	const handleAdd = async () => {
		setIsAdding(true);

		try {
			const created = await addMissingDefaults({ entityType }).unwrap();

			if (!created.length) {
				handleOpenSnackbar(ts("addMissingDefaultsNoneAdded"));
			}
		} catch {
			handleOpenSnackbar(ts("addMissingDefaultsFailed"));
		} finally {
			setIsAdding(false);
		}
	};

	if (!PIPEDRIVE_TABS_WITH_DEFAULTS.includes(entityType)) {
		return null;
	}

	return (
		<>
			{renderMessageSnackbar()}
			<span title={ts("addMissingDefaultsTooltip")}>
				<Button
					color={ButtonColor.ACTION_SECONDARY}
					image={<Icon className="mr-2 h-[16px] w-[16px] text-text" icon={faRotateLeft} />}
					isLoading={isAdding}
					size={ButtonSize.S}
					testId="pipedrive-add-missing-defaults"
					title={ts("addMissingDefaults")}
					onClick={handleAdd}
				/>
			</span>
		</>
	);
};
